import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { getAuthHeaders } from "../api";

// Auth-aware image lightbox with wheel-zoom, click-drag pan, and
// double-click toggle. No external library — all math is done
// client-side against CSS transforms on the <img>.
//
// This is the single lightbox used across the app: the older
// Lightbox.tsx (non-zoomable, bare <a href> download links) was
// merged into this component. When callers provide `filename`, the
// caption strip with Open original / Download buttons is shown —
// when omitted, the component renders as a plain zoom viewer (used
// by the Repair Dates modal, which doesn't want extra chrome).
//
// Rendered over whatever parent triggered it. The parent passes the
// R2-backed URL via `src`; we fetch the blob with Bearer auth (the
// /images endpoint can't accept bare <img> requests), wrap it in an
// object URL, then hand that to the <img>. Object URL is revoked on
// unmount.
//
// Close behaviour:
//   - Click the dark backdrop area (outside the image)
//   - Press Escape
//   - Click the explicit Close button
// All three stop event propagation so the parent modal (which may
// have its own click-outside-to-close handler) is never triggered.

interface Props {
  src: string;
  onClose: () => void;
  // When supplied, renders the caption strip with filename + "Open
  // original" / "Download" actions. Omitting it hides the strip —
  // the Repair Dates modal uses the bare zoom viewer.
  filename?: string;
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;
const DOUBLE_CLICK_SCALE = 2;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export default function ZoomableLightbox({ src, onClose, filename }: Props) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState(false);
  // Hold the raw Blob so the "Open original" / "Download" actions
  // can mint their own object URLs — the displayed one is revoked
  // on unmount, which would break a download link opened in a new
  // tab after the lightbox closes.
  const blobRef = useRef<Blob | null>(null);
  // Transform state, applied to the <img>. tx/ty are in CSS pixels
  // relative to the centred origin set by `translate(-50%, -50%)`.
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  // Pointer drag bookkeeping. `didDragRef` gets flipped once the
  // pointer moves a few pixels so a pure click on the image doesn't
  // fire the close handler on pointerup.
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startTx: number;
    startTy: number;
  } | null>(null);
  const didDragRef = useRef(false);
  // Active touch points, keyed by pointerId, for pinch/two-finger pan.
  const pinchRef = useRef<
    | null
    | {
        a: { id: number; x: number; y: number };
        b: { id: number; x: number; y: number };
        startDist: number;
        startScale: number;
        startMidX: number;
        startMidY: number;
        startTx: number;
        startTy: number;
      }
  >(null);

  // --- fetch blob + object URL ---
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setObjectUrl(null);
    setFetchError(false);
    blobRef.current = null;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(src, {
          credentials: "include",
          headers,
        });
        if (!res.ok) throw new Error(`http_${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        blobRef.current = blob;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      } catch {
        if (!cancelled) setFetchError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [src]);

  // --- Escape closes the lightbox (and stops propagation so the
  //     parent modal's Escape handler doesn't also close the modal) ---
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      onClose();
    }
    // Capture phase so we beat the parent modal's window-level
    // keydown listener, which is registered in the bubble phase.
    window.addEventListener("keydown", onKey, true);
    return () =>
      window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // --- Lock scroll while open. Without this a wheel event over the
  //     backdrop scrolls the page behind. ---
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // --- reset transform when the image changes ---
  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, [src]);

  const reset = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  // Zoom centred on a point (px/py, relative to container centre).
  // New translation must keep the same image-space point under the
  // cursor: if the cursor is at (px, py) relative to the container's
  // centred transform origin, the currently-visible image-space
  // coordinate there is (px - tx) / scale. After scaling to k, that
  // coordinate should land back at (px, py), i.e.
  //   newTx = px - (px - tx) * (k / scale)
  const zoomAt = useCallback(
    (nextScale: number, px: number, py: number) => {
      const k = nextScale / scale;
      setScale(nextScale);
      setTx((prev) => px - (px - prev) * k);
      setTy((prev) => py - (py - prev) * k);
    },
    [scale],
  );

  function containerCentreRelative(
    clientX: number,
    clientY: number,
  ): { px: number; py: number } {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { px: 0, py: 0 };
    return {
      px: clientX - (rect.left + rect.width / 2),
      py: clientY - (rect.top + rect.height / 2),
    };
  }

  const onWheel = useCallback(
    (e: ReactWheelEvent<HTMLDivElement>) => {
      e.preventDefault();
      const { px, py } = containerCentreRelative(e.clientX, e.clientY);
      // deltaY > 0 = wheel down = zoom out; flip sign. 0.0015 is
      // a comfortable sensitivity on trackpads without feeling
      // sluggish on mice.
      const factor = Math.exp(-e.deltaY * 0.0015);
      const next = clamp(scale * factor, MIN_SCALE, MAX_SCALE);
      zoomAt(next, px, py);
    },
    [scale, zoomAt],
  );

  // --- Pointer down: start a drag (mouse) or register for pinch (touch) ---
  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "touch") {
        // Track up to two touches for pinch/two-finger pan.
        if (!pinchRef.current) {
          pinchRef.current = {
            a: { id: e.pointerId, x: e.clientX, y: e.clientY },
            b: { id: -1, x: 0, y: 0 },
            startDist: 0,
            startScale: scale,
            startMidX: 0,
            startMidY: 0,
            startTx: tx,
            startTy: ty,
          };
        } else if (pinchRef.current.b.id === -1) {
          const a = pinchRef.current.a;
          const b = { id: e.pointerId, x: e.clientX, y: e.clientY };
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          pinchRef.current.b = b;
          pinchRef.current.startDist = Math.hypot(dx, dy);
          pinchRef.current.startScale = scale;
          const midClientX = (a.x + b.x) / 2;
          const midClientY = (a.y + b.y) / 2;
          const { px, py } = containerCentreRelative(midClientX, midClientY);
          pinchRef.current.startMidX = px;
          pinchRef.current.startMidY = py;
          pinchRef.current.startTx = tx;
          pinchRef.current.startTy = ty;
        }
        return;
      }
      // Mouse / pen: click-drag to pan.
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startTx: tx,
        startTy: ty,
      };
      didDragRef.current = false;
      (e.target as Element).setPointerCapture(e.pointerId);
    },
    [scale, tx, ty],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // Touch path: if two fingers are active, pinch-zoom.
      if (e.pointerType === "touch" && pinchRef.current) {
        const p = pinchRef.current;
        if (e.pointerId === p.a.id) p.a = { ...p.a, x: e.clientX, y: e.clientY };
        else if (e.pointerId === p.b.id)
          p.b = { ...p.b, x: e.clientX, y: e.clientY };
        else return;
        if (p.b.id === -1) {
          // Single finger while we're tracking — treat as pan.
          const dx = p.a.x - p.startMidX;
          const dy = p.a.y - p.startMidY;
          setTx(p.startTx + dx);
          setTy(p.startTy + dy);
          return;
        }
        const dist = Math.hypot(p.b.x - p.a.x, p.b.y - p.a.y);
        if (p.startDist > 0) {
          const factor = dist / p.startDist;
          const next = clamp(
            p.startScale * factor,
            MIN_SCALE,
            MAX_SCALE,
          );
          const midClientX = (p.a.x + p.b.x) / 2;
          const midClientY = (p.a.y + p.b.y) / 2;
          const { px, py } = containerCentreRelative(
            midClientX,
            midClientY,
          );
          // Two-finger pan: offset between the current midpoint and
          // the midpoint at pinch-start.
          const panX = px - p.startMidX;
          const panY = py - p.startMidY;
          const k = next / p.startScale;
          setScale(next);
          setTx(p.startMidX - (p.startMidX - p.startTx) * k + panX);
          setTy(p.startMidY - (p.startMidY - p.startTy) * k + panY);
        }
        return;
      }
      // Mouse / pen drag.
      if (!dragRef.current || e.pointerId !== dragRef.current.pointerId)
        return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) didDragRef.current = true;
      setTx(dragRef.current.startTx + dx);
      setTy(dragRef.current.startTy + dy);
    },
    [],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "touch") {
        if (!pinchRef.current) return;
        if (e.pointerId === pinchRef.current.b.id) {
          pinchRef.current.b = { id: -1, x: 0, y: 0 };
          // Capture current state as the new "start" for any
          // continuing single-finger pan.
          pinchRef.current.startTx = tx;
          pinchRef.current.startTy = ty;
          pinchRef.current.startMidX = pinchRef.current.a.x;
          pinchRef.current.startMidY = pinchRef.current.a.y;
        } else if (e.pointerId === pinchRef.current.a.id) {
          pinchRef.current = null;
        }
        return;
      }
      if (dragRef.current && e.pointerId === dragRef.current.pointerId) {
        dragRef.current = null;
      }
    },
    [tx, ty],
  );

  // Double-click toggles between fit-to-screen (1x) and 2x zoom at
  // the click point.
  const onDoubleClick = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const { px, py } = containerCentreRelative(e.clientX, e.clientY);
      if (scale > 1.01) {
        reset();
      } else {
        zoomAt(DOUBLE_CLICK_SCALE, px, py);
      }
    },
    [scale, reset, zoomAt],
  );

  // Click-outside-to-close is handled on the stage, not the backdrop:
  // the stage covers the backdrop entirely (`inset: 0`), so any real
  // click lands on it. We close only when:
  //   1. The drag tracker says the user didn't just pan, AND
  //   2. The click's target IS the stage element itself (i.e. the
  //      user clicked empty space beside the image, not the image).
  // Both checks must pass, otherwise the click is either a drag
  // finishing or a tap on the image — neither should close.
  // stopPropagation is the important bit: without it the click
  // bubbles into the parent modal's click-outside handler and
  // closes the whole repair modal.
  const onStageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (didDragRef.current) {
        didDragRef.current = false;
        e.stopPropagation();
        return;
      }
      if (e.target === e.currentTarget) {
        e.stopPropagation();
        onClose();
      }
      // Click on image (target !== currentTarget) — do nothing,
      // the user may be setting up a double-click. Still don't let
      // it bubble up to the parent modal.
      else {
        e.stopPropagation();
      }
    },
    [onClose],
  );

  // --- Auth-aware Open original / Download actions. Both mint a
  //     fresh object URL from the in-memory Blob so that revoking
  //     the display-side URL on unmount doesn't break anything the
  //     user opened.
  //
  // "Open original" intentionally doesn't revoke — the new tab owns
  // the blob lifecycle and the browser will GC the object URL when
  // that tab is closed. "Download" revokes after a short delay
  // because the synthetic <a> click only needs the URL for an
  // instant. ---
  const hasBlob = !!blobRef.current;

  const onOpenOriginal = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const blob = blobRef.current;
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Leak the URL intentionally — see comment above.
    },
    [],
  );

  const onDownload = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const blob = blobRef.current;
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename ?? "image";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    [filename],
  );

  // Clicks inside the controls / backdrop must never reach the
  // parent modal's click-outside handler. `swallow` is also used on
  // the outer backdrop as a belt-and-braces guard in case anything
  // reaches it (the stage should cover it, but z-index edges).
  const swallow = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      ref={containerRef}
      className="zoom-lightbox"
      onClick={swallow}
      role="presentation"
    >
      <div
        className={`zoom-lightbox-stage${scale > 1.01 ? " zoomed" : ""}`}
        onClick={onStageClick}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
      >
        {fetchError ? (
          <div className="zoom-lightbox-error">Couldn't load image.</div>
        ) : !objectUrl ? (
          <div className="zoom-lightbox-loading" />
        ) : (
          <img
            src={objectUrl}
            alt={filename ?? ""}
            className="zoom-lightbox-img"
            draggable={false}
            style={{
              transform: `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${scale})`,
            }}
          />
        )}
      </div>
      {filename && (
        <div className="zoom-lightbox-caption" onClick={swallow}>
          <span className="zoom-lightbox-filename">{filename}</span>
          <div className="zoom-lightbox-caption-actions">
            <button
              type="button"
              className="ui-btn ui-btn-secondary ui-btn-sm"
              onClick={onOpenOriginal}
              disabled={!hasBlob}
              title={hasBlob ? "Open the original in a new tab" : "Loading…"}
            >
              {hasBlob ? "Open original" : "Loading…"}
            </button>
            <button
              type="button"
              className="ui-btn ui-btn-secondary ui-btn-sm"
              onClick={onDownload}
              disabled={!hasBlob}
              title={hasBlob ? "Download to your device" : "Loading…"}
            >
              {hasBlob ? "Download" : "Loading…"}
            </button>
          </div>
        </div>
      )}
      <div className="zoom-lightbox-controls" onClick={swallow}>
        <span className="zoom-lightbox-level">
          {Math.round(scale * 100)}%
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            reset();
          }}
          disabled={scale === 1 && tx === 0 && ty === 0}
        >
          Reset
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
